/**
 * Página pública para compartir el nivel alcanzado por un futbolista
 * 
 * URL: /share/futbolista/:futbolistaId/nivel
 * 
 * Muestra el nivel actual del futbolista con diseño optimizado
 * para compartir en redes sociales
 */

import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import '../../styles/share-logros.css';

const ShareNivel = () => {
  const { futbolistaId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchNivel = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `/api/postgres/share/futbolista/${futbolistaId}/nivel`
        );

        if (response.ok) {
          const result = await response.json();
          if (result.success) {
            setData(result.data);
          } else {
            setError(result.message);
          }
        } else {
          setError('No se pudo cargar el nivel');
        }
      } catch (err) {
        console.error('Error cargando nivel:', err);
        setError('Error al cargar el nivel');
      } finally {
        setLoading(false);
      }
    };

    fetchNivel();
  }, [futbolistaId]);

  if (loading) {
    return (
      <div className="share-logros-container">
        <div className="share-logros-loading">
          <div className="spinner"></div>
          <p>Cargando nivel...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="share-logros-container">
        <div className="share-logros-error">
          <h2>❌ Error</h2>
          <p>{error || 'No se encontró el nivel'}</p>
        </div>
      </div>
    );
  }

  const { futbolista, nivel, propoints_acumulados, temporada } = data;
  const shareUrl = data.share_url || window.location.href;
  const pageTitle = `${futbolista.nombre} - ${nivel.nombre} | Liga ProKids`;
  const pageDescription = `¡${futbolista.nombre} ha alcanzado el nivel ${nivel.nombre}! ${propoints_acumulados} ProPoints acumulados en Liga ProKids.`;

  // Detectar si es móvil
  // eslint-disable-next-line no-unused-vars
  const isMobile = window.innerWidth <= 768;

  // Imagen para Open Graph (usar icono del nivel o imagen por defecto)
  const ogImage = `${window.location.origin}/images/logo-ligaprokids.png`;

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={shareUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content={ogImage} />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={shareUrl} />
        <meta property="twitter:title" content={pageTitle} />
        <meta property="twitter:description" content={pageDescription} />
        <meta property="twitter:image" content={ogImage} />
      </Helmet>

      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: isMobile ? '12px' : '20px'
      }}>
        <div style={{
          maxWidth: isMobile ? '100%' : '500px',
          width: '100%',
          background: 'white',
          borderRadius: isMobile ? '20px' : '24px',
          padding: isMobile ? '24px 20px' : '40px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          textAlign: 'center'
        }}>
          {/* Información del Futbolista */}
          <h1 style={{
            fontSize: isMobile ? '1.6rem' : '2rem',
            color: '#061156',
            marginBottom: '6px',
            fontWeight: '700',
            lineHeight: '1.2'
          }}>
            {futbolista.nombre}
          </h1>
          <p style={{
            color: '#666',
            fontSize: isMobile ? '0.9rem' : '1rem',
            marginBottom: isMobile ? '20px' : '24px'
          }}>
            Temporada {temporada.nombre}
          </p>

          {/* Icono del Nivel - GRANDE */}
          <div style={{
            margin: isMobile ? '30px auto' : '40px auto',
            fontSize: isMobile ? '120px' : '150px',
            lineHeight: '1'
          }}>
            {nivel.icono}
          </div>

          {/* Detalles del Nivel */}
          <div style={{
            marginTop: isMobile ? '20px' : '24px',
            padding: isMobile ? '20px' : '24px',
            background: '#F8F9FF',
            borderRadius: isMobile ? '16px' : '20px',
            border: '3px solid #E8ECFF'
          }}>
            <div style={{
              fontSize: isMobile ? '0.85rem' : '0.95rem',
              color: '#4352A9',
              fontWeight: '600',
              marginBottom: '8px',
              textTransform: 'uppercase',
              letterSpacing: '1px'
            }}>
              🎯 Nivel Alcanzado
            </div>
            <h2 style={{
              fontSize: isMobile ? '1.8rem' : '2.2rem',
              color: nivel.color || '#061156',
              marginBottom: '12px',
              fontWeight: '700',
              lineHeight: '1.2'
            }}>
              {nivel.nombre}
            </h2>
            {nivel.descripcion && (
              <p style={{
                color: '#666',
                fontSize: isMobile ? '0.95rem' : '1.05rem',
                marginBottom: '20px',
                lineHeight: '1.5',
                fontStyle: 'italic'
              }}>
                {nivel.descripcion}
              </p>
            )}

            {/* ProPoints Acumulados */}
            <div style={{
              display: 'inline-block',
              background: 'linear-gradient(135deg, #4CAF50 0%, #2E7D32 100%)',
              color: 'white',
              padding: isMobile ? '14px 28px' : '16px 32px',
              borderRadius: isMobile ? '12px' : '14px',
              fontSize: isMobile ? '1.3rem' : '1.5rem',
              fontWeight: 'bold',
              boxShadow: '0 6px 16px rgba(76, 175, 80, 0.4)',
              marginTop: '10px'
            }}>
              {propoints_acumulados} ProPoints
            </div>
          </div>

          {/* Mensaje de Felicitación */}
          <div style={{
            marginTop: isMobile ? '24px' : '30px',
            padding: isMobile ? '16px' : '20px',
            background: 'linear-gradient(135deg, #FFF9C4 0%, #FFF59D 100%)',
            borderRadius: isMobile ? '12px' : '16px',
            border: '2px solid #F9A825'
          }}>
            <p style={{
              margin: 0,
              color: '#F57F17',
              fontSize: isMobile ? '1rem' : '1.1rem',
              fontWeight: '600',
              lineHeight: '1.4'
            }}>
              🎉 ¡Felicidades por alcanzar este nivel!
            </p>
            <p style={{
              margin: '8px 0 0 0',
              color: '#666',
              fontSize: isMobile ? '0.85rem' : '0.9rem',
              lineHeight: '1.4'
            }}>
              Sigue acumulando ProPoints para desbloquear nuevos niveles
            </p>
          </div>

          {/* Call to Action */}
          <div style={{ marginTop: isMobile ? '24px' : '30px' }}>
            <a
              href="https://ligaprokids.com"
              style={{
                display: 'inline-block',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                padding: isMobile ? '14px 32px' : '16px 40px',
                borderRadius: isMobile ? '12px' : '14px',
                textDecoration: 'none',
                fontSize: isMobile ? '1rem' : '1.1rem',
                fontWeight: '600',
                boxShadow: '0 6px 16px rgba(102, 126, 234, 0.4)',
                transition: 'transform 0.2s',
                cursor: 'pointer'
              }}
              onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
              onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
              target="_blank"
              rel="noopener noreferrer"
            >
              Únete a Liga ProKids
            </a>
          </div>
        </div>
      </div>
    </>
  );
};

export default ShareNivel;

